import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PricingSection } from '../components/pricing-section';
import { Navbar1 } from '../components/shadcnblocks-com-navbar1';
import { AuroraBackground } from '../components/ui/aurora-background';
import { Button } from '../components/ui/button';
import { Footer } from '../components/ui/footer';
import { footerData } from '../data/navegacion';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';


const Plans = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [paymentMessage, setPaymentMessage] = useState('');

  // Las dos funciones van con useCallback y el efecto las declara como
  // dependencia. Antes el efecto corría con `[currentUser]` y las dejaba
  // afuera: se quedaba con la versión de la función del primer render, que
  // es la que todavía no tenía usuario.
  //
  // El orden importa: `handlePaymentReturn` nombra a
  // `verifyPaymentAndUpdatePlan` en sus dependencias, así que tiene que estar
  // definida antes (con `const` no hay hoisting que valga).

  const verifyPaymentAndUpdatePlan = useCallback(async (paymentId) => {
    try {
      if (!currentUser) return;
      
      const token = await currentUser.getIdToken();
      // La ruta es /api/mercadopago/verify-payment (server.js). Acá decía
      // /api/plan/verify-payment, que no existe: al volver de Mercado Pago la
      // verificación devolvía 404 y el plan nunca se actualizaba.
      const response = await fetch(`${API_URL}/api/mercadopago/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paymentId }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Plan actualizado exitosamente:', data);
          // Forzar refresh del plan
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    }
  }, [currentUser]);

  const handlePaymentReturn = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentId = urlParams.get('payment_id');
    const status = urlParams.get('status');
    
    if (paymentStatus === 'success' || status === 'approved') {
      setPaymentMessage('✅ ¡Pago exitoso! Tu plan ha sido actualizado correctamente.');
      
      if (paymentId) {
        verifyPaymentAndUpdatePlan(paymentId);
      }
      
      // Limpiar URL params después de un tiempo
      setTimeout(() => {
        const url = new URL(window.location);
        url.searchParams.delete('payment');
        url.searchParams.delete('payment_id');
        url.searchParams.delete('status');
        window.history.replaceState({}, document.title, url.toString());
        setPaymentMessage('');
      }, 5000);
      
    } else if (paymentStatus === 'cancelled') {
      setPaymentMessage('❌ Pago cancelado. Puedes intentarlo nuevamente cuando quieras.');
      
      setTimeout(() => {
        const url = new URL(window.location);
        url.searchParams.delete('payment');
        window.history.replaceState({}, document.title, url.toString());
        setPaymentMessage('');
      }, 5000);
    }
  }, [verifyPaymentAndUpdatePlan]);

  useEffect(() => {
    handlePaymentReturn();
  }, [handlePaymentReturn]);

  // Configuración del navbar para Aturno
  const navbarConfig = {
    logo: {
      url: "/",
      src: "",
      alt: "Aturno Logo",
      title: "Aturno",
    },
    menu: [
      // Anclas de la landing con la barra adelante, para que funcionen también
      // desde esta página. Antes esto apuntaba a /caracteristicas, /ayuda,
      // /configuracion, /precios y /acerca-de, ninguna de las cuales existe
      // en App.js: el catch-all /:slug las convertía en páginas rotas.
      { title: "Inicio", url: "/" },
      { title: "Funcionalidades", url: "/#caracteristicas" },
      { title: "Planes", url: "/#precios" },
      { title: "Preguntas", url: "/#recursos" },
      { title: "Demo", url: "/demo" },
    ],
    mobileExtraLinks: [
      // Solo rutas que existen en App.js. Antes esto apuntaba a
      // /contacto, /soporte, /blog y /status, que no existen y que el
      // catch-all /:slug convertía en páginas de negocio rotas.
      { name: "Ver demo", url: "/demo" },
      { name: "Crear cuenta", url: "/auth/register" },
    ],
    auth: {
      login: { text: "Iniciar Sesión", url: "/auth/login" },
      signup: { text: "Registrarse", url: "/auth/register" },
    },
  };

  // Configuración del footer para Aturno

  return (
    <AuroraBackground
      showRadialGradient={true}
      showGrid={false}
      showGlow={true}
      animated={true}
    >
      {/* Esta página no tiene hoja de estilos propia y antes se maquetaba con
          seis clases (`plans-page-new`, `payment-message-section`,
          `payment-message`, `back-section`, `back-container`, `back-btn`) que no
          existían en ningún lado: la única definida es `.page-header .back-btn`,
          en las hojas de Términos y Privacidad, o sea que acá tampoco aplicaba.
          Por eso "Volver al Dashboard" era un botón crudo del navegador colgado
          abajo de las tarjetas. Va todo con las utilidades y el <Button> de la
          app, que es lo que usa el resto de las pantallas nuevas. */}
      <div className="relative z-10">
        {/* Navbar de shadcn/ui */}
        <Navbar1 {...navbarConfig} />

        <div className="mx-auto w-full max-w-6xl px-4 pb-16 pt-10 sm:px-6">
          {/* Payment Message */}
          {paymentMessage && (
            <div
              role="status"
              className={`mb-6 rounded-2xl border px-5 py-4 text-sm font-medium ${
                paymentMessage.includes('exitoso')
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              {paymentMessage}
            </div>
          )}

          {/* La caja blanca: los planes se apoyan sobre el fondo animado en vez
              de taparlo. El fondo lo pone AuroraBackground y la sección venía
              con `bg-background` de punta a punta, así que no se veía nunca. */}
          <div className="rounded-3xl bg-white p-5 shadow-xl ring-1 ring-black/5 sm:p-8">
            <PricingSection
              showCurrentPlan={true}
              // El relleno de página completa que trae la sección sobra adentro
              // de la caja, que ya pone el suyo.
              className="py-0 md:py-0 lg:py-0"
            />
          </div>

          <div className="mt-8 flex justify-center">
            <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
              ← Volver al panel
            </Button>
          </div>
        </div>

        {/* Footer */}
        <Footer {...footerData} />
      </div>
    </AuroraBackground>
  );
};

export default Plans;